/**
 * "Aufräumen": arranges several elements into a tidy row, column or grid with
 * equal gaps, inside the bounding box they already occupy. Pure geometry on
 * axis-aligned bounding rects; the caller moves each element by the delta.
 */

type Rect = { x: number; y: number; width: number; height: number };

export type TidyItem = { id: string; rect: Rect };

/** Number of columns that fits the items' current spread best. */
export function tidyColumns(items: readonly TidyItem[], bounds: Rect): number {
  const n = items.length;
  if (n <= 1) return 1;
  const maxWidth = Math.max(...items.map((item) => item.rect.width));
  const maxHeight = Math.max(...items.map((item) => item.rect.height));
  // Already (roughly) one row or one column: keep it that way.
  if (bounds.height < maxHeight * 1.5) return n;
  if (bounds.width < maxWidth * 1.5) return 1;
  const avgWidth = items.reduce((sum, item) => sum + item.rect.width, 0) / n;
  const avgHeight = items.reduce((sum, item) => sum + item.rect.height, 0) / n;
  const estimate = Math.sqrt((n * (bounds.width / bounds.height) * avgHeight) / avgWidth);
  // Among the column counts that fit into the bounds, the one closest to the
  // estimate; when none fits (overlapping items), the one that overflows least.
  let best = 1;
  let bestScore = Number.POSITIVE_INFINITY;
  for (let columns = 1; columns <= n; columns++) {
    const rows = Math.ceil(n / columns);
    const overflow = Math.max(
      (columns * maxWidth) / bounds.width,
      (rows * maxHeight) / bounds.height,
    );
    const score = overflow <= 1 ? Math.abs(columns - estimate) : 1000 * overflow;
    if (score < bestScore) {
      best = columns;
      bestScore = score;
    }
  }
  return best;
}

/** Reading order: rows from top to bottom (by centre), left to right within a row. */
function readingOrder(items: readonly TidyItem[], columns: number): TidyItem[] {
  const centreY = (item: TidyItem) => item.rect.y + item.rect.height / 2;
  const centreX = (item: TidyItem) => item.rect.x + item.rect.width / 2;
  const byY = [...items].sort((a, b) => centreY(a) - centreY(b) || centreX(a) - centreX(b));
  const out: TidyItem[] = [];
  for (let i = 0; i < byY.length; i += columns) {
    out.push(...byY.slice(i, i + columns).sort((a, b) => centreX(a) - centreX(b)));
  }
  return out;
}

/**
 * New top-left corners (of the bounding rects) per id. Columns are as wide as
 * their widest item, rows as high as their highest; items sit centred in their
 * cell. The gaps are equal and fill the current bounds; when the items do not
 * fit, a small gap is used and the arrangement grows around the same centre.
 */
export function tidyLayout(
  items: readonly TidyItem[],
  options: { columns?: number; minGap?: number } = {},
): Map<string, { x: number; y: number }> {
  const out = new Map<string, { x: number; y: number }>();
  if (items.length === 0) return out;
  const left = Math.min(...items.map((item) => item.rect.x));
  const top = Math.min(...items.map((item) => item.rect.y));
  const right = Math.max(...items.map((item) => item.rect.x + item.rect.width));
  const bottom = Math.max(...items.map((item) => item.rect.y + item.rect.height));
  const bounds = { x: left, y: top, width: right - left, height: bottom - top };
  const columns = Math.max(
    1,
    Math.min(items.length, options.columns ?? tidyColumns(items, bounds)),
  );
  const rows = Math.ceil(items.length / columns);
  const ordered = readingOrder(items, columns);
  const colWidths = new Array<number>(columns).fill(0);
  const rowHeights = new Array<number>(rows).fill(0);
  ordered.forEach((item, index) => {
    const col = index % columns;
    const row = Math.floor(index / columns);
    colWidths[col] = Math.max(colWidths[col]!, item.rect.width);
    rowHeights[row] = Math.max(rowHeights[row]!, item.rect.height);
  });
  const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);
  const minGap =
    options.minGap ??
    Math.max(
      4,
      Math.round(Math.min(...items.map((i) => Math.min(i.rect.width, i.rect.height))) * 0.1),
    );
  const gapFor = (space: number, used: number, count: number) =>
    count > 1 ? Math.max(minGap, (space - used) / (count - 1)) : 0;
  // One row or column: fill that direction; a grid uses the same gap both ways.
  let gapX = gapFor(bounds.width, sum(colWidths), columns);
  let gapY = gapFor(bounds.height, sum(rowHeights), rows);
  if (columns > 1 && rows > 1) gapX = gapY = Math.min(gapX, gapY);
  const usedWidth = sum(colWidths) + gapX * (columns - 1);
  const usedHeight = sum(rowHeights) + gapY * (rows - 1);
  // Centred on the old bounds (also when it has to grow: overlapping items).
  const originX = left + (bounds.width - usedWidth) / 2;
  const originY = top + (bounds.height - usedHeight) / 2;
  const colX: number[] = [];
  let cursor = originX;
  for (const width of colWidths) {
    colX.push(cursor);
    cursor += width + gapX;
  }
  const rowY: number[] = [];
  cursor = originY;
  for (const height of rowHeights) {
    rowY.push(cursor);
    cursor += height + gapY;
  }
  ordered.forEach((item, index) => {
    const col = index % columns;
    const row = Math.floor(index / columns);
    out.set(item.id, {
      x: colX[col]! + (colWidths[col]! - item.rect.width) / 2,
      y: rowY[row]! + (rowHeights[row]! - item.rect.height) / 2,
    });
  });
  return out;
}
